import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Skeleton, cn } from "@pagespeedify/ui";
import { ArrowUpRight, ChevronDown, Gauge } from "lucide-react";
import { Link } from "react-router-dom";
import { useApi } from "../../lib/apiClient";
import { useAuth } from "../../lib/auth/AuthContext";
import type { AgentUsageFeatureInfo, AgentUsageResponse } from "../../types/agent";

interface ChatLimitsWidgetProps {
  className?: string;
  compact?: boolean;
  panelClassName?: string;
}

function getPercentRemaining(feature?: AgentUsageFeatureInfo): number | null {
  if (!feature) return null;
  const remaining = Number(feature.remainingInBundle || 0);
  const limit = Number(feature.limitForPeriod || 0);
  if (!Number.isFinite(remaining) || !Number.isFinite(limit) || limit <= 0) {
    return null;
  }
  return Math.max(0, Math.min(100, (Math.max(0, remaining) / limit) * 100));
}

function formatPercentRemaining(percentRemaining: number | null): string | null {
  if (percentRemaining === null || !Number.isFinite(percentRemaining)) return null;
  const flooredPercent = Math.floor(percentRemaining * 10 + 1e-6) / 10;
  return Number.isInteger(flooredPercent) ? `${flooredPercent}%` : `${flooredPercent.toFixed(1)}%`;
}

function formatRenewalDate(periodEndDate?: string | Date): string | null {
  if (!periodEndDate) return null;
  const parsed = periodEndDate instanceof Date ? periodEndDate : new Date(periodEndDate);
  if (!Number.isFinite(parsed.getTime())) return null;

  const now = new Date();
  const includeYear = parsed.getFullYear() !== now.getFullYear();
  const formatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    ...(includeYear ? { year: "numeric" } : {}),
  });

  return formatter.format(parsed);
}

function getUpgradeLabel(userType?: string | null): string | null {
  switch ((userType || "").toLowerCase()) {
    case "basic":
      return "Upgrade to Pro";
    case "pro":
      return "Upgrade to Enterprise";
    case "free":
    case "unsubscribed":
      return "View Plans";
    default:
      return null;
  }
}

export default function ChatLimitsWidget({
  className,
  compact = false,
  panelClassName,
}: ChatLimitsWidgetProps) {
  const apiClient = useApi();
  const { user } = useAuth();
  const userId = user?.uid || null;
  const isFreeLikeUser =
    (user?.user_type || "").toLowerCase() === "unsubscribed" ||
    (user?.user_type || "").toLowerCase() === "free";
  const [limitsExpanded, setLimitsExpanded] = useState(false);

  const usageQuery = useQuery<AgentUsageResponse>({
    queryKey: ["agent-usage", userId || "anonymous"],
    queryFn: () => apiClient.get("/agent/usage"),
    enabled: !!userId && !isFreeLikeUser,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const usageData = usageQuery.data?.success ? usageQuery.data.usage : undefined;
  const computeUnits = usageData?.computeUnits;
  const percentRemaining = useMemo(() => getPercentRemaining(computeUnits), [computeUnits]);
  const percentRemainingLabel = useMemo(
    () => formatPercentRemaining(percentRemaining),
    [percentRemaining]
  );
  const renewalLabel = useMemo(
    () =>
      formatRenewalDate(
        usageData?.periodEndDate ||
          user?.subscription?.current_period_end ||
          user?.subscription?.next_billing_at
      ),
    [usageData?.periodEndDate, user?.subscription?.current_period_end, user?.subscription?.next_billing_at]
  );
  const upgradeLabel = useMemo(() => getUpgradeLabel(user?.user_type), [user?.user_type]);

  const containerClassName = cn(
    compact ? "w-full text-xs" : "w-full text-sm",
    panelClassName,
    className
  );

  return (
    <div className={containerClassName}>
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-xs font-medium text-foreground transition-colors hover:bg-muted/60"
        onClick={() => setLimitsExpanded((prev) => !prev)}
      >
        <span className="flex items-center gap-2">
          <Gauge className="h-3.5 w-3.5 text-muted-foreground" />
          <span>Rate limits remaining</span>
        </span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 text-muted-foreground transition-transform",
            limitsExpanded ? "rotate-180" : ""
          )}
        />
      </button>

      {limitsExpanded && (
        <div className="px-2.5 pb-1 pt-1.5">
          {usageQuery.isLoading ? (
            <div className="space-y-2 px-1 py-1">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-20" />
            </div>
          ) : usageQuery.isError || !usageData || !percentRemainingLabel ? (
            <div className="space-y-2 px-1 py-1 text-xs text-muted-foreground">
              <div>{isFreeLikeUser ? "Upgrade to view Agent usage" : "Usage summary unavailable"}</div>
              {upgradeLabel && (
                <Link
                  to="/subscriptions"
                  className="inline-flex items-center gap-1.5 font-medium text-foreground transition-colors hover:text-primary"
                >
                  {upgradeLabel}
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-2 px-1 py-1 text-xs">
              <div className="grid grid-cols-[1fr_auto_auto] items-center gap-3">
                <span className="font-medium text-foreground">Monthly</span>
                <span className="text-sm font-semibold text-foreground">{percentRemainingLabel}</span>
                {renewalLabel ? (
                  <span className="text-muted-foreground">{renewalLabel}</span>
                ) : (
                  <span />
                )}
              </div>
              {upgradeLabel && (
                <Link
                  to="/subscriptions"
                  className="inline-flex items-center gap-1.5 font-medium text-foreground transition-colors hover:text-primary"
                >
                  {upgradeLabel}
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
